import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';

interface DeletedPregnancyState {
  showModal: boolean;
  closeModal: () => void;
}

/**
 * Hook to check if the user's partner connection has a 'pregnancy_deleted' status.
 * If found, shows a modal to inform them and cleans up the connection.
 * After closing the modal, OnboardingGuard will redirect to /onboarding automatically.
 */
export function useDeletedPregnancyCheck(): DeletedPregnancyState {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [deletedConnectionId, setDeletedConnectionId] = useState<string | null>(null);

  // Check for deleted pregnancy connections on mount
  useEffect(() => {
    const checkDeletedConnections = async () => {
      if (!user?.id) return;

      try {
        const { data: connections, error } = await supabase
          .from('partner_connections')
          .select('id')
          .eq('user_2_id', user.id)
          .eq('status', 'pregnancy_deleted');

        if (error) {
          console.error('Error checking deleted connections:', error);
          return;
        }

        if (connections && connections.length > 0) {
          // Found a deleted pregnancy connection
          setDeletedConnectionId(connections[0].id);
          setShowModal(true);
        }
      } catch (err) {
        console.error('Error checking deleted connections:', err);
      }
    };

    checkDeletedConnections();
  }, [user?.id]);

  const closeModal = useCallback(async () => {
    // Clean up the deleted connection when modal is closed
    if (deletedConnectionId) {
      await supabase
        .from('partner_connections')
        .delete()
        .eq('id', deletedConnectionId);
    }
    setShowModal(false);
    setDeletedConnectionId(null);
    // OnboardingGuard will detect no pregnancy and redirect to /onboarding
  }, [deletedConnectionId]);

  return {
    showModal,
    closeModal,
  };
}
